import { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { FileText, Maximize2, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL || '';

const PDFViewer = ({ url, title }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(700);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const containerRef = useRef(null);

  if (!url) return null;

  const buildPdfUrl = () => {
    let pdfPath = url;
    if (pdfPath.startsWith('/uploads/')) {
      pdfPath = pdfPath.substring('/uploads/'.length);
    } else if (pdfPath.startsWith('/api/uploads/')) {
      pdfPath = pdfPath.substring('/api/uploads/'.length);
    }
    return `${API}/api/pdf-view/${pdfPath}`;
  };

  const pdfSrc = buildPdfUrl();

  // Reset states when URL changes
  useEffect(() => {
    setError(false);
    setUseFallback(false);
    setPageNumber(1);
    setNumPages(null);
    setScale(1.0);
  }, [url]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setError(false);
  };

  const onDocumentLoadError = (err) => {
    console.warn('PDFViewer react-pdf error:', err?.message || err);
    setError(true);
    // Auto-switch to iframe fallback
    setUseFallback(true);
  };

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 32);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [fullscreen]);

  const goToPrev = () => setPageNumber(p => Math.max(1, p - 1));
  const goToNext = () => setPageNumber(p => Math.min(numPages || p, p + 1));
  const zoomIn = () => setScale(s => Math.min(2.5, s + 0.2));
  const zoomOut = () => setScale(s => Math.max(0.5, s - 0.2));

  const pageWidth = Math.min(containerWidth, 900) * scale;

  // ===== Iframe fallback viewer =====
  if (useFallback) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#d0e8f0]" data-testid="pdf-viewer-fallback">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-[#35AFE4]" />
            <h3 className="text-base font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
              {title || 'Document PDF'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <a href={pdfSrc} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#eaf6fc] text-[#35AFE4] text-xs font-semibold rounded-lg hover:bg-[#d0e8f0] transition-colors"
              data-testid="pdf-open-new-tab">
              <Maximize2 size={12} /> Ouvrir
            </a>
            <a href={pdfSrc} download
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1e3a5f] text-white text-xs font-semibold rounded-lg hover:bg-[#2a5a8f] transition-colors"
              data-testid="pdf-download">
              <Download size={12} /> Télécharger
            </a>
          </div>
        </div>
        <iframe
          src={pdfSrc}
          title={title || 'PDF'}
          className="w-full rounded-xl border border-gray-200"
          style={{ height: '700px' }}
          data-testid="pdf-iframe"
        />
      </div>
    );
  }

  // ===== react-pdf viewer =====
  const viewer = (isFullscreen) => (
    <div ref={!isFullscreen ? containerRef : undefined}>
      <Document
        file={pdfSrc}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4] mb-3" />
            <p className="text-xs text-gray-400">Chargement du document...</p>
          </div>
        }
      >
        <Page
          pageNumber={pageNumber}
          width={isFullscreen ? Math.min(window.innerWidth * 0.85, 1200) * scale : pageWidth}
          renderTextLayer={true}
          renderAnnotationLayer={true}
        />
      </Document>
    </div>
  );

  return (
    <>
      {/* Normal viewer */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#d0e8f0]" data-testid="pdf-viewer">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-[#35AFE4]" />
            <h3 className="text-base font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
              {title || 'Document PDF'}
            </h3>
            {numPages && <span className="text-xs text-gray-400 ml-2">{numPages} page{numPages > 1 ? 's' : ''}</span>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={zoomOut} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" title="Dézoomer" data-testid="pdf-zoom-out"><ZoomOut size={16} /></button>
            <span className="text-xs text-gray-400 min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" title="Zoomer" data-testid="pdf-zoom-in"><ZoomIn size={16} /></button>
            <button onClick={() => setFullscreen(true)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors ml-1" title="Plein écran" data-testid="pdf-fullscreen"><Maximize2 size={16} /></button>
            <a href={pdfSrc} download className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" title="Télécharger" data-testid="pdf-download"><Download size={16} /></a>
          </div>
        </div>

        <div className="overflow-auto rounded-xl bg-gray-50 border border-gray-200" style={{ maxHeight: '600px' }}>
          {viewer(false)}
        </div>

        {numPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-3">
            <button onClick={goToPrev} disabled={pageNumber <= 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 text-gray-600 transition-colors" data-testid="pdf-prev"><ChevronLeft size={18} /></button>
            <span className="text-sm font-medium text-gray-700" data-testid="pdf-page-info">{pageNumber} / {numPages}</span>
            <button onClick={goToNext} disabled={pageNumber >= numPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 text-gray-600 transition-colors" data-testid="pdf-next"><ChevronRight size={18} /></button>
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="pdf-fullscreen-overlay">
          <div className="flex items-center justify-between px-6 py-3 bg-black/40">
            <div className="flex items-center gap-2 text-white">
              <FileText size={16} />
              <span className="text-sm font-bold">{title}</span>
              {numPages && <span className="text-xs text-white/50 ml-2">{pageNumber} / {numPages}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={zoomOut} className="p-2 text-white/60 hover:text-white transition-colors"><ZoomOut size={16} /></button>
              <span className="text-xs text-white/50">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} className="p-2 text-white/60 hover:text-white transition-colors"><ZoomIn size={16} /></button>
              <button onClick={() => { setFullscreen(false); setScale(1.0); }} className="p-2 text-white/60 hover:text-white transition-colors ml-2"><X size={18} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex justify-center items-start p-4">
            {viewer(true)}
          </div>
          {numPages > 1 && (
            <div className="flex items-center justify-center gap-4 py-3 bg-black/40">
              <button onClick={goToPrev} disabled={pageNumber <= 1} className="p-2 text-white/60 hover:text-white disabled:opacity-30 transition-colors"><ChevronLeft size={20} /></button>
              <span className="text-sm text-white">{pageNumber} / {numPages}</span>
              <button onClick={goToNext} disabled={pageNumber >= numPages} className="p-2 text-white/60 hover:text-white disabled:opacity-30 transition-colors"><ChevronRight size={20} /></button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PDFViewer;
